import React,
       { useEffect,
         useRef,
         useCallback,
         useMemo
       }                                    from 'react'
import { Box,
         TextField,
         FormHelperText,
         FormControl,
         Select,
         MenuItem,
         FormControlLabel,
         Checkbox,
         Typography,
         Grid,
         Alert,
         useTheme,
         darken,
       }                                    from '@mui/material'
import { useForm, 
         Controller 
       }                                    from 'react-hook-form'
import { APIData }                          from 'types/cms/frontendTypes'
import { CreateSurveyQuestionRequestData, 
         SurveyQuestionLocalization 
       }                                    from 'types/apis/surveyInterfaces'
import { AnswerChoicesSection }             from './AnswerChoicesSection'
import { AnswerChoice }                     from 'types/apis/surveyInterfaces'
import { SurveyQBUtils }                    from '../utils/SurveyQBUtils'
import { isEqual, omit }                    from 'lodash'
import { useGlobalConfig }                  from 'context/GlobalContext'
import { useMapperCatalog }                 from './mapperBuilder/useMapperCatalog'
import { HpStateMapperPicker }              from './mapperBuilder/HpStateMapperPicker'
import { ApStateMapperPicker }              from './mapperBuilder/ApStateMapperPicker'
import { deriveQuestionLinks }              from './mapperSerializer'
import { ConvertToStructuredBanner }        from './mapperBuilder/ConvertToStructuredBanner'
import { QuestionScopeSelector }            from './mapperBuilder/QuestionScopeSelector'
import { MapperScopeConstraint }            from './mapperBuilder/cascadeTypes'
import { scopeTargetChanged,
         reconcileChoicesToScope }          from './mapperBuilder/reconcileScope'
import { useSelector, useDispatch }         from 'react-redux'
import { selectCMSSelectedPilot, 
         selectCMSLocales, 
         CMS_DEFAULT_LOCALE 
       }                                    from 'store/dataReducers/cmsPilotSlice'
import { selectSurveyCategories, selectTableState, 
         setSelectedLocaleAction 
       }                                    from 'store/dataReducers/surveyQBSlice'

export interface SurveyQuestionFormProps {
  question?: APIData<'api::survey-question.survey-question'>
  publishedQuestion?: APIData<'api::survey-question.survey-question'>
  isReadOnly: boolean
  onFormStateChange: (isDirty: boolean) => void
  onSubmit: (data: any) => void
  onFormReady?: (submitFn: () => void) => void
}

const QUESTION_TYPES = [
  'SINGLE',
  'MULTIPLE',
  'MULTIPLE_WITH_SUB_CHOICES',
  'TEXT',
  'NUMBER',
  'IMAGE_UPLOAD',
  'TEXTAREA',
  'BOOLEAN',
  'RANGE',
  'SLIDER',
  'TWO_DIMENSIONS',
  'THREE_DIMENSIONS',
] as const

const GROUPS = ['ESSENTIAL', 'DETAILED'] as const
const PROFILE_CODES = ['AP', 'HP', 'METADATA'] as const
const FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER'] as const

// DA-2300 "Convert to structured" migration CTA (ConvertToStructuredBanner) — intentionally
// OFF for now (product decision: not needed yet). The banner, its reverse-parse logic, and its
// tests are all kept intact; flip this to true to re-enable, or wire it to a runtime config/flag
// if per-environment control is ever wanted. This is the single switch that hides the CTA.
const ENABLE_CONVERT_TO_STRUCTURED_CTA = false

// DA-2184: CREATE mode starts with the required Selects unselected so the author picks each
// one deliberately (no silent SINGLE/DETAILED/METADATA defaults). maxLevel + GLOBAL fuel keep
// their defaults. EDIT mode is unaffected — it reset(...)s every field from the question.
const defaultFormValues = {
  title: '',
  info: '',
  type: '',
  group: '',
  category: '',
  profileCode: '',
  stateMapper: '',
  maxLevel: 1,
  fuelTypes: ['GLOBAL'] as string[],
  impactShc: false,
  impactItemization: false,
  impactReco: false,
  choices: [],
  localizations: [] as SurveyQuestionLocalization[],
} as unknown as Partial<CreateSurveyQuestionRequestData>

export const SurveyQuestionForm: React.FC<SurveyQuestionFormProps> = ({
  question,
  publishedQuestion,
  isReadOnly,
  onFormStateChange,
  onSubmit,
  onFormReady,
}) => {

  const formRef = useRef<HTMLFormElement>(null)
  const theme = useTheme()
  const dispatch = useDispatch()
  const { rc } = useGlobalConfig()

  const selectedPilot = useSelector(selectCMSSelectedPilot)
  const tableState    = useSelector(selectTableState)
  const locales       = useSelector(selectCMSLocales)!
  const categories    = useSelector(selectSurveyCategories)!

  // DA-2184: the selected pilot IS a utility, so it carries the account type — HP attributes are
  // filtered to it (RESIDENTIAL/SMB) so the pickers only offer this segment's attributes.
  const { catalog: mapperCatalog, loading: mapperCatalogLoading, error: mapperCatalogError } =
    useMapperCatalog(rc, (selectedPilot as any)?.accountType)

  const selectedLocale = tableState.selectedLocale

  const { control, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue, getValues, trigger, setError } = useForm({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  } as any) as any

  // Store handleSubmit, trigger, and onFormSubmit in refs to avoid stale closures
  const handleSubmitRef = useRef(handleSubmit)
  const triggerRef = useRef(trigger)
  const onFormSubmitRef = useRef<((data: any) => void) | null>(null)
  
  // Keep refs updated with latest values on every render
  handleSubmitRef.current = handleSubmit
  triggerRef.current = trigger

  const questionType = watch('type')
  // SM-18: profileCode is the top-level form switch. METADATA hides choices + mapper
  // controls; HP/AP each reveal their branch. Watched so the form reacts on change.
  const profileCode = watch('profileCode')
  const category = watch('category')

  // DA-2184: an AP question's appliance scope is restricted to the appliances the selected SURVEY
  // CATEGORY maps to (survey-category.applianceIds). undefined when the category has no configured
  // list — then the scope picker is unrestricted (no regression for un-migrated categories).
  const categoryApplianceIds = React.useMemo<number[] | undefined>(() => {
    const ids = (categories.find((c) => c.categoryId === category) as { applianceIds?: unknown } | undefined)?.applianceIds
    return Array.isArray(ids) ? ids.map(Number).filter((n) => Number.isFinite(n)) : undefined
  }, [categories, category])

  const [isGlobal, setIsGlobal] = React.useState<boolean>(false)
  const [selectedFuelTypes, setSelectedFuelTypes] = React.useState<string[]>([])
  const [answerChoices, setAnswerChoices] = React.useState<AnswerChoice[]>([])

  // Helper function to get locale-specific value for top-level fields
  const getLocaleValue = (question: APIData<'api::survey-question.survey-question'>, fieldName: 'title' | 'info', locale: string): string => {
    if (locale === CMS_DEFAULT_LOCALE) {
      return (question[fieldName] as string) || '';
    }
    
    const localizations: SurveyQuestionLocalization[] = (question as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === locale) || {} as SurveyQuestionLocalization;
    return (localeData[fieldName] as string) || '';
  };

  // Helper function to get locale-specific choice title
  const getChoiceLocaleTitle = (choice: any, choiceIndex: number, locale: string, question: APIData<'api::survey-question.survey-question'>): string => {
    if (locale === CMS_DEFAULT_LOCALE) {
      return choice.title || '';
    }
    
    const localizations: SurveyQuestionLocalization[] = (question as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === locale);
    if (localeData && localeData.choices && localeData.choices[choiceIndex]) {
      return localeData.choices[choiceIndex].title || '';
    }
    return '';
  };

  // Helper function to get locale-specific subchoice title
  const getSubChoiceLocaleTitle = (subChoice: any, choiceIndex: number, subChoiceIndex: number, locale: string, question: APIData<'api::survey-question.survey-question'>): string => {
    if (locale === CMS_DEFAULT_LOCALE) {
      return subChoice.title || '';
    }
    
    const localizations: SurveyQuestionLocalization[] = (question as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === locale);
    if (localeData && localeData.choices && localeData.choices[choiceIndex] && localeData.choices[choiceIndex].subChoices && localeData.choices[choiceIndex].subChoices![subChoiceIndex]) {
      return localeData.choices[choiceIndex].subChoices![subChoiceIndex].title || '';
    }
    return '';
  };

  // Helper function to set locale-specific value and update localizations in form state
  const setLocaleTextValue = (fieldName: 'title' | 'info', value: string) => {
    // Always update the form field so user sees their changes immediately
    setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
    
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // Default locale - form field update is sufficient
      return;
    }
    
    // Non-default locale - also update localizations in form state
    const allValues = getValues();
    const existingLocalizations: SurveyQuestionLocalization[] = allValues.localizations || [];
    const existingIndex = existingLocalizations.findIndex((l: SurveyQuestionLocalization) => l.locale === selectedLocale);

    let updatedLocalizations: SurveyQuestionLocalization[];
    if (existingIndex >= 0) {
      // Update existing locale entry
      const existing = existingLocalizations[existingIndex];
      const updated: SurveyQuestionLocalization = {
        ...existing,
        [fieldName]: value,
      };
      updatedLocalizations = [
        ...existingLocalizations.slice(0, existingIndex),
        updated,
        ...existingLocalizations.slice(existingIndex + 1)
      ];
    } else {
      // Create new locale entry
      const newLocaleEntry: SurveyQuestionLocalization = {
        locale: selectedLocale,
        title: fieldName === 'title' ? value : (allValues.title || ''),
        ...(fieldName === 'info' ? { info: value } : {}),
        choices: [],
      };
      updatedLocalizations = [...existingLocalizations, newLocaleEntry];
    }

    setValue('localizations', updatedLocalizations, { shouldDirty: true, shouldValidate: true });
  };

  // Helper function to set locale-specific choices and update localizations in form state
  const setLocaleChoicesValue = (choices: AnswerChoice[]) => {
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // Default locale - choices are stored in main choices field
      // This is handled by handleChoicesChange
      return;
    }
    
    // Non-default locale - update localizations[].choices in form state
    const allValues = getValues();
    const existingLocalizations: SurveyQuestionLocalization[] = allValues.localizations || [];
    const existingIndex = existingLocalizations.findIndex((l: SurveyQuestionLocalization) => l.locale === selectedLocale);

    // Convert choices to localized format (without order, readMapper, etc.)
    // DA-2184: the choice `value` is derived from the Display Text — which in a non-default
    // locale is the LOCALIZED title. Localized rows must still key off the DEFAULT-locale value
    // (EN and its translations share one value), so resolve it from the loaded question's
    // choices by index; fall back to the local value only in create mode (no question yet).
    const localizedChoices = choices
      .map((choice, index) => ({
        choice,
        enValue: (question?.choices?.[index]?.value ?? choice.value ?? '') as string,
      }))
      .filter(({ choice, enValue }) => enValue.trim() !== '' && (choice.title || '').trim() !== '')
      .map(({ choice, enValue }) => {
        const localizedSubChoices = (choice.subChoices || [])
          .filter((sc: any) => sc.value && sc.value.trim() !== '')
          .map((subChoice: any) => ({
            title: subChoice.title || '',
            value: subChoice.value || '',
            selectionType: subChoice.selectionType,
            subChoiceValidationType: subChoice.subChoiceValidationType,
            allowedValues: subChoice.allowedValues,
          }));

        return {
          title: choice.title || '',
          value: enValue,
          ...(localizedSubChoices.length > 0 ? { subChoices: localizedSubChoices } : {}),
        };
      });

    let updatedLocalizations: SurveyQuestionLocalization[];
    if (existingIndex >= 0) {
      // Update existing locale entry
      const existing = existingLocalizations[existingIndex];
      const updated: SurveyQuestionLocalization = {
        ...existing,
        choices: localizedChoices,
      };
      updatedLocalizations = [
        ...existingLocalizations.slice(0, existingIndex),
        updated,
        ...existingLocalizations.slice(existingIndex + 1)
      ];
    } else {
      // Create new locale entry
      const newLocaleEntry: SurveyQuestionLocalization = {
        locale: selectedLocale,
        title: allValues.title || '',
        ...(allValues.info ? { info: allValues.info } : {}),
        choices: localizedChoices,
      };
      updatedLocalizations = [...existingLocalizations, newLocaleEntry];
    }

    setValue('localizations', updatedLocalizations, { shouldDirty: true, shouldValidate: true });
  };

  // Handle locale change
  const handleLocaleChange = (newLocale: string) => {
    dispatch(setSelectedLocaleAction(newLocale));
  };

  // Initialize form when question data is provided
  useEffect(() => {
    if (question) {
      // Parse fuel type - if it's GLOBAL, set isGlobal to true, otherwise parse comma-separated
      const fuelTypes = (question.fuelTypes || []) as string[]
      if (fuelTypes.includes('GLOBAL')) {
        setIsGlobal(true)
        setSelectedFuelTypes([])
      } else {
        setIsGlobal(false)
        setSelectedFuelTypes(fuelTypes)
      }

      // Convert choices to AnswerChoice format with locale-specific titles
      const choices: AnswerChoice[] = (question.choices || []).map((choice: any, index: number) => ({
        title: getChoiceLocaleTitle(choice, index, selectedLocale, question) || choice.title || '',
        value: choice.value,
        order: choice.order,
        readMapper: choice.readMapper,
        writeMapper: choice.writeMapper,
        unselectMapper: choice.unselectMapper,
        // SM-25: carry the structured mapper model through load. Without these the
        // guided builder opens empty on a question that already has clauses, and the
        // diff has nothing on the current side to compare against published.
        mapperSource: choice.mapperSource,
        hpMapperClauses: choice.hpMapperClauses,
        apMapperClauses: choice.apMapperClauses,
        subChoices: (choice.subChoices || []).map((subChoice: any, subIndex: number) => ({
          ...subChoice,
          title: getSubChoiceLocaleTitle(subChoice, index, subIndex, selectedLocale, question) || subChoice.title || '',
        })),
      }))

      setAnswerChoices(choices)

      reset({
        title: getLocaleValue(question, 'title', selectedLocale),
        type: question.type,
        group: question.group,
        category: question.category,
        profileCode: question.profileCode,
        stateMapper: question.stateMapper,
        maxLevel: question.maxLevel ?? 1,
        fuelTypes: question.fuelTypes,
        impactShc: question.impactShc,
        impactItemization: question.impactItemization,
        impactReco: question.impactReco,
        choices: choices,
        verificationStatus: question.verificationStatus,
        info: getLocaleValue(question, 'info', selectedLocale),
        localizations: question.localizations || [],
        // SM-26: question-level structured mapper model. No UI authors these yet (the
        // choiceless HP picker writes stateMapper directly), but they must survive the
        // load -> save round trip or editing anything else would drop them.
        mapperSource: (question as any).mapperSource,
        homeProfileLinks: (question as any).homeProfileLinks,
        applianceLinks: (question as any).applianceLinks,
      })
      } else {
       reset(defaultFormValues)
       setIsGlobal(true)
       setSelectedFuelTypes([])
       setAnswerChoices([])
     }
   }, [question, reset, selectedLocale])

  // Track form changes and notify parent
  useEffect(() => {
    onFormStateChange?.(isDirty)
  }, [isDirty, onFormStateChange])

  const handleGlobalChange = (checked: boolean) => {
    setIsGlobal(checked)
    if (checked) {
      setSelectedFuelTypes([])
      setValue('fuelTypes', ['GLOBAL'] as string[], { shouldDirty: true })
    }
  }

  const handleFuelTypeChange = (fuelType: string, checked: boolean) => {
    if (isGlobal) {
      // If GLOBAL is selected, uncheck it when selecting a specific fuel type
      setIsGlobal(false)
    }

    let newSelected: string[]
    if (checked) {
      newSelected = [...selectedFuelTypes, fuelType]
    } else {
      newSelected = selectedFuelTypes.filter(ft => ft !== fuelType)
    }

    setSelectedFuelTypes(newSelected)

    // Store as comma-separated string (never empty - if empty, should be GLOBAL)
    const fuelTypes = newSelected.length === 0 ? ['GLOBAL'] : newSelected
    setValue('fuelTypes', fuelTypes as string[], { shouldDirty: true })
  }

  const handleChoicesChange = (choices: AnswerChoice[]) => {
    setAnswerChoices(choices)
    
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // Default locale - update form choices field directly
      setValue('choices', choices as any, { shouldDirty: true })
    } else {
      // Non-default locale - update localizations in form state
      setLocaleChoicesValue(choices)
      // Also update form choices field for display (though it won't be used on submit)
      setValue('choices', choices as any, { shouldDirty: true })
    }
  }

  // Update form fields and choices when locale changes to show locale-specific values
  useEffect(() => {
    if (question) {
      // Get locale-specific values - check form state localizations first (for unsaved edits),
      // then fall back to question's localizations
      const formValues = getValues();
      const formLocalizations = formValues.localizations || [];
      const formLocaleData = formLocalizations.find((l: SurveyQuestionLocalization) => l.locale === selectedLocale);
      
      // Get title - prefer form state, then question
      const titleValue = selectedLocale === CMS_DEFAULT_LOCALE
        ? (formValues.title || getLocaleValue(question, 'title', selectedLocale))
        : (formLocaleData?.title || getLocaleValue(question, 'title', selectedLocale));
      
      // Get info - prefer form state, then question
      const infoValue = selectedLocale === CMS_DEFAULT_LOCALE
        ? (formValues.info || getLocaleValue(question, 'info', selectedLocale))
        : (formLocaleData?.info || getLocaleValue(question, 'info', selectedLocale));
      
      setValue('title', titleValue, { shouldDirty: false })
      setValue('info', infoValue, { shouldDirty: false })
      
      // Update choices with locale-specific titles
      if (answerChoices.length > 0) {
        const updatedChoices = answerChoices.map((choice, index) => {
          // For non-default locale, check form state localizations first
          let choiceTitle = choice.title;
          if (selectedLocale !== CMS_DEFAULT_LOCALE && formLocaleData?.choices?.[index]) {
            choiceTitle = formLocaleData.choices[index].title || choiceTitle;
          } else {
            choiceTitle = getChoiceLocaleTitle(choice, index, selectedLocale, question) || choiceTitle;
          }
          
          const updatedSubChoices = (choice.subChoices || []).map((subChoice: any, subIndex: number) => {
            let subChoiceTitle = subChoice.title;
            if (selectedLocale !== CMS_DEFAULT_LOCALE && formLocaleData?.choices?.[index]?.subChoices?.[subIndex]) {
              subChoiceTitle = formLocaleData.choices[index].subChoices![subIndex].title || subChoiceTitle;
            } else {
              subChoiceTitle = getSubChoiceLocaleTitle(subChoice, index, subIndex, selectedLocale, question) || subChoiceTitle;
            }
            return {
              ...subChoice,
              title: subChoiceTitle,
            };
          });
          
          return {
            ...choice,
            title: choiceTitle,
            subChoices: updatedSubChoices,
          }
        })
        setAnswerChoices(updatedChoices)
        setValue('choices', updatedChoices as any, { shouldDirty: false })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocale])

  const shouldShowChoices = ['SINGLE', 'MULTIPLE', 'MULTIPLE_WITH_SUB_CHOICES'].includes(questionType)
  // SM-18: a METADATA answer is free text stored as survey metadata — no choices, no mappers.
  const isMetadataProfile = profileCode === 'METADATA'
  const showChoicesSection = shouldShowChoices && !isMetadataProfile
  // SM-22: choiceless HP questions have no choices to build clauses on — stateMapper
  // IS the whole read+write mapper, and it's a single attribute (HP-<path>).
  const isChoicelessHpQuestion = profileCode === 'HP' && ['NUMBER', 'TWO_DIMENSIONS', 'THREE_DIMENSIONS'].includes(questionType)
  // SM-23: same for choiceless AP — stateMapper is the whole mapper (AP:<id>-<op>[:key]).
  const isChoicelessApQuestion = profileCode === 'AP' && ['NUMBER', 'TWO_DIMENSIONS', 'THREE_DIMENSIONS'].includes(questionType)

  // A question WITH choices doesn't author stateMapper at all — its links are the distinct
  // targets its choices' clauses touch, and the BE re-derives the string from them on save
  // (SM-9 / SM-11). Mirror that derivation live so the admin watches the value build up as
  // choices are added or edited, rather than staring at an empty field until after a save.
  const derivedQuestionLinks = useMemo(() => deriveQuestionLinks(answerChoices), [answerChoices])
  const hasDerivedStateMapper =
    derivedQuestionLinks.homeProfileLinks.length > 0 || derivedQuestionLinks.applianceLinks.length > 0
  // A choiced HP/AP question NEVER authors its own stateMapper — the BE derives it from the
  // choices on save, whether or not any clauses exist yet. So the field is read-only from the
  // start (not just once clauses appear), never an editable text box.
  const stateMapperIsDerived = showChoicesSection && (profileCode === 'HP' || profileCode === 'AP')

  // DA-2184 forward guard: a question-level scope pins every choice picker's TARGET
  // (attribute column / appliance id). The author sets it explicitly via QuestionScopeSelector;
  // when unset it is reverse-populated from the choices' clauses, so an already-authored
  // question surfaces a consistent target rather than nothing.
  const [questionScope, setQuestionScope] = React.useState<MapperScopeConstraint | undefined>(undefined)
  const scopeFromLinks = (links: ReturnType<typeof deriveQuestionLinks>): MapperScopeConstraint | undefined => {
    if (links.homeProfileLinks.length > 0) {
      return { profileCode: 'HP', attributePath: links.homeProfileLinks[0].attributePath }
    }
    if (links.applianceLinks.length > 0) {
      // The scope is the distinct SET of appliances the choices touch — a choice may then
      // combine them into an `&`/`|` group.
      const ids = [...new Set(links.applianceLinks.map((l) => l.applianceId))]
      return { profileCode: 'AP', applianceIds: ids }
    }
    return undefined
  }
  const effectiveScope = questionScope ?? scopeFromLinks(derivedQuestionLinks)

  // DA-2184: when the question-level scope TARGET changes (or is removed), every choice's READ
  // clauses must follow it — otherwise a clause stays pinned to the attribute/appliance the
  // author just moved away from (the "previous one persists" bug), and clearing the scope leaves
  // the choices still scoped. The retarget (and the value reset it implies) is skipped when the
  // target is unchanged. Logic lives in reconcileScope.ts so it's unit-testable in isolation.
  const handleScopeChange = (next: MapperScopeConstraint | undefined) => {
    const prev = effectiveScope
    setQuestionScope(next)
    if (!scopeTargetChanged(prev, next)) return
    setAnswerChoices(reconcileChoicesToScope(answerChoices, next))
  }

  // SM-27: a legacy MANUAL question — hand-typed strings with no structured model behind
  // them. Offer the reverse parse only when there is something to convert and no
  // structured work would be overwritten by the prefill.
  const hasLegacyChoiceStrings = useMemo(
    () => answerChoices.some((c) => c.readMapper || c.writeMapper || c.unselectMapper),
    [answerChoices],
  )
  const canConvertToStructured =
    ENABLE_CONVERT_TO_STRUCTURED_CTA &&
    !isReadOnly &&
    (profileCode === 'HP' || profileCode === 'AP') &&
    hasLegacyChoiceStrings &&
    !hasDerivedStateMapper

  // Validate choices before submission
  // Only validate if choices exist - allow questions without choices
  const validateChoices = useCallback((): boolean => {
    if (shouldShowChoices && answerChoices.length > 0) {
      // If choices exist, validate they are complete
      return answerChoices.every(choice => choice.title.trim() !== '' && choice.value.trim() !== '')
    }
    // No choices or not a choice-based question type - validation passes
    return true
  }, [shouldShowChoices, answerChoices])

  const onFormSubmit = useCallback((data: any) => {
    // Validate choices before submission
    // Only validate if choices exist - allow questions without choices
    const choicesValid = validateChoices()
    if (!choicesValid) {
      // Set error on choices field to show user
      // This only happens if choices exist but are incomplete
      if (shouldShowChoices && answerChoices.length > 0) {
        setError('choices', { 
          type: 'manual', 
          message: 'All answer choices must have both title and value' 
        }, { shouldFocus: true })
      }
      return
    }

    // Get all form values (following RecoEditFormComponent pattern)
    const allValues = getValues() as CreateSurveyQuestionRequestData

    // Get default locale choices
    // - Default locale: use answerChoices (already has default locale titles)
    // - Non-default locale (edit mode): use question.choices (default locale from question)
    // - Non-default locale (create mode): use answerChoices (user's input, treat as default locale)
    const getDefaultLocaleChoices = (): AnswerChoice[] => {
      if (selectedLocale === CMS_DEFAULT_LOCALE) {
        return answerChoices
      } else {
        // For non-default locale, use question's choices if editing, otherwise use answerChoices
        return (question?.choices && question.choices.length > 0) 
          ? question.choices.map((choice: any, index: number) => ({
              title: choice.title || '',
              value: choice.value || '',
              order: choice.order ?? index + 1, // Use ?? to preserve 0 values
              readMapper: choice.readMapper,
              writeMapper: choice.writeMapper,
              unselectMapper: choice.unselectMapper,
              // SM-26: preserve the structured mapper model when saving from a
              // non-default locale — the mappers aren't localized, so dropping them
              // here would wipe them for every locale.
              mapperSource: choice.mapperSource,
              hpMapperClauses: choice.hpMapperClauses,
              apMapperClauses: choice.apMapperClauses,
              subChoices: (choice.subChoices || []).map((sc: any, scIndex: number) => ({
                title: sc.title || '',
                value: sc.value || '',
                order: sc.order ?? scIndex + 1, // Use ?? to preserve 0 values
                selectionType: sc.selectionType,
                subChoiceValidationType: sc.subChoiceValidationType,
                allowedValues: sc.allowedValues,
              })),
            }))
          : answerChoices // Create mode - use user's input
      }
    }

    // Build default locale choices - filter and ensure proper structure
    // Normalize to match the structure expected by the diff utility
    const defaultLocaleChoices = getDefaultLocaleChoices()
      .filter(choice => {
        // Filter out invalid choices
        if (choice.value.trim() === '') return false
        // For default locale, also require title
        if (selectedLocale === CMS_DEFAULT_LOCALE && choice.title.trim() === '') return false
        return true
      })
      .map((choice, index) => {
        // SM-26: an AUTO choice's read/write/unselect strings are derived server-side
        // from the structured clauses (SM-11). The client must never author them — omit
        // them from the payload entirely so a stale or hand-typed string can't reach the
        // server. MANUAL choices keep sending exactly what the admin typed.
        const isAuto = choice.mapperSource === 'AUTO'
        // DA-2184: authors write ONLY the READ intent; the BE derives write/unselect on
        // save. Filter an AUTO choice's clauses to role==='READ' before sending so any
        // legacy WRITE/UNSELECT clauses are dropped and never diff as a false change. This
        // is the single payload choke point both the default- and non-default-locale branches
        // flow through. MANUAL rows keep their clauses exactly as loaded.
        const readOnlyClauses = <T extends { role?: string }>(clauses?: T[]): T[] | undefined =>
          clauses && isAuto ? clauses.filter((c) => c.role === 'READ') : clauses
        const hpClausesOut = readOnlyClauses(choice.hpMapperClauses)
        const apClausesOut = readOnlyClauses(choice.apMapperClauses)
        return {
          title: choice.title || '',
          value: choice.value || '',
          ...(isAuto ? {} : {
            readMapper: choice.readMapper,
            writeMapper: choice.writeMapper,
            unselectMapper: choice.unselectMapper,
          }),
          // Structured mapper model — only sent when the choice actually has one, so
          // untouched legacy rows keep the exact shape they were loaded with.
          ...(choice.mapperSource ? { mapperSource: choice.mapperSource } : {}),
          ...(hpClausesOut ? { hpMapperClauses: hpClausesOut } : {}),
          ...(apClausesOut ? { apMapperClauses: apClausesOut } : {}),
          order: choice.order ?? index + 1, // Use ?? to preserve 0 values, normalize undefined/null
          subChoices: (choice.subChoices || []).map((sc: any, scIndex: number) => ({
            title: sc.title || '',
            value: sc.value || '',
            order: sc.order ?? scIndex + 1, // Use ?? to preserve 0 values, normalize undefined/null
            selectionType: sc.selectionType,
            subChoiceValidationType: sc.subChoiceValidationType,
            allowedValues: sc.allowedValues,
          })),
        }
      })

    // Get localizations from form state (following RecoEditFormComponent pattern)
    // Localizations are already updated in form state via setLocaleTextValue and setLocaleChoicesValue
    const localizations: SurveyQuestionLocalization[] = allValues.localizations || []

    // Question-level structured mapper — the same contract SM-26 established for choices,
    // one level up. The links are the distinct targets the choices address; the BE derives
    // stateMapper from them for AUTO rows (SM-9 / SM-11), so send the model and drop the
    // string entirely rather than letting a stale client value reach the server.
    const questionLinks = deriveQuestionLinks(defaultLocaleChoices)
    const hasQuestionLinks =
      questionLinks.homeProfileLinks.length > 0 || questionLinks.applianceLinks.length > 0

    // Build complete form data (following RecoEditFormComponent pattern)
    const formData: any = {
      ...allValues,
      choices: defaultLocaleChoices as any,
      ...(hasQuestionLinks ? {
        mapperSource: 'AUTO',
        homeProfileLinks: questionLinks.homeProfileLinks,
        applianceLinks: questionLinks.applianceLinks,
      } : {}),
      // Only include localizations if they exist and have content
      // This prevents sending empty arrays when nothing changed
      ...(localizations.length > 0 ? { localizations } : {}),
    }

    // METADATA has no HP/AP target — its stateMapper is `METADATA-<template id>`, computed by the
    // template API at render time (the id depends on where the question lands in the template), so
    // never persist a client value for it. HP/AP with choice links already drop it just above.
    if (hasQuestionLinks || allValues.profileCode === 'METADATA') delete formData.stateMapper

    // Only override title/info for non-default locale (to preserve default locale values from question)
    // For default locale, allValues already has the correct values
    if (selectedLocale !== CMS_DEFAULT_LOCALE) {
      if (question) {
        // Edit mode - preserve default locale values from question
        formData.title = question.title || ''
        formData.info = question.info
      } else {
        // Create mode in non-default locale - set empty defaults
        formData.title = ''
        formData.info = undefined
      }
    }

    onSubmit(formData)
  }, [
    validateChoices,
    shouldShowChoices,
    answerChoices,
    setError,
    getValues,
    selectedLocale,
    question,
    onSubmit
  ])

  // Keep onFormSubmit ref updated whenever the function is recreated
  useEffect(() => {
    onFormSubmitRef.current = onFormSubmit
  }, [onFormSubmit])

  // Expose submit function to parent via callback
  // Recreate submit function whenever onFormReady or onFormSubmit changes
  // This ensures the submit function always has access to the latest onFormSubmit
  useEffect(() => {
    if (onFormReady && onFormSubmitRef.current) {
      const submitFn = async () => {
        if (onFormSubmitRef.current && handleSubmitRef.current) {
          try {
            // Trigger validation first to ensure form is valid
            const isValid = await triggerRef.current()
            
            if (isValid && onFormSubmitRef.current) {
              // handleSubmit returns a function that validates and calls the callback
              const submitHandler = handleSubmitRef.current(
                (data: any) => {
                  onFormSubmitRef.current!(data)
                },
                (errors: any) => {
                  // Validation errors are handled by react-hook-form
                  console.error('Form validation errors:', errors)
                }
              )
              // Call without event for programmatic submission
              submitHandler()
            }
          } catch (error) {
            console.error('Error submitting form:', error)
          }
        }
      }
      onFormReady(submitFn)
    }
  }, [onFormReady, onFormSubmit]) // Recreate when onFormSubmit changes to ensure latest version

  // Get display text for group
  const getGroupDisplayText = (group: string) => {
    return group
  }

  // Common label component for FormControl elements
  const renderFormLabel = (label: string) => (
    <Typography 
      variant="body2" 
      sx={{ 
        mb: 1, 
        color: theme.palette.text.secondary,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  )

  // Add function to check if field is modified (for localized fields)
  const isFieldModified = (fieldName: keyof CreateSurveyQuestionRequestData) => {
    // Only show modifications in edit mode when we have both question and published question
    if (!question || !publishedQuestion) {
      return false;
    }

    // For localized fields (title, info), compare locale-specific values
    if (fieldName === 'title' || fieldName === 'info') {
      const formValues = getValues() as CreateSurveyQuestionRequestData;
      const currentValue = formValues[fieldName] || '';
      const publishedValue = getLocaleValue(publishedQuestion, fieldName as 'title' | 'info', selectedLocale);
      return currentValue !== publishedValue;
    }

    // For non-localized fields, use the standard diff comparison
    const formValues = getValues() as CreateSurveyQuestionRequestData;
    const currentFormData: CreateSurveyQuestionRequestData = {
      ...formValues,
    };

    // Compare current form values with published question to detect modifications
    const diff = SurveyQBUtils.getQuestionDiff(currentFormData, publishedQuestion as any) as any;
    return Object.keys(diff).includes(fieldName);
  }

  // Helper function to check if a specific choice field is modified (handles localization)
  const isChoiceFieldModified = (choiceIndex: number, fieldName: keyof AnswerChoice): boolean => {
    if (!question || !publishedQuestion) {
      return false;
    }

    // For title field, compare locale-specific values
    if (fieldName === 'title') {
      const currentTitle = answerChoices[choiceIndex]?.title || '';
      const publishedTitle = getChoiceLocaleTitle(
        publishedQuestion.choices?.[choiceIndex] || {},
        choiceIndex,
        selectedLocale,
        publishedQuestion
      );
      return currentTitle !== publishedTitle;
    }

    // For other fields, compare directly
    const formValues = getValues() as CreateSurveyQuestionRequestData;
    const currentChoices = formValues.choices || [];
    const publishedChoices = publishedQuestion.choices || [];

    // If choices array length changed, all fields in this choice are considered modified
    if (currentChoices.length !== publishedChoices.length) {
      return choiceIndex < currentChoices.length;
    }

    // If choice at this index doesn't exist in published, it's new/modified
    if (choiceIndex >= publishedChoices.length) {
      return true;
    }

    const currentChoice = currentChoices[choiceIndex];
    const publishedChoice = publishedChoices[choiceIndex];

    if (!currentChoice || !publishedChoice) {
      return false;
    }

    // Compare the specific field
    return !isEqual(currentChoice[fieldName], publishedChoice[fieldName]);
  }

  // SM-25: the guided builder edits structured clauses, and for AUTO choices the raw
  // read/write/unselect strings are re-derived server-side on save (SM-11) — so they
  // don't change locally and their per-field highlights never fire. Roll the structured
  // fields up into one "this choice's mapper changed" signal for the builder entry point.
  const isChoiceMapperModified = (choiceIndex: number): boolean => {
    if (!question || !publishedQuestion) {
      return false;
    }

    const currentChoices = (getValues() as CreateSurveyQuestionRequestData).choices || [];
    const publishedChoices = publishedQuestion.choices || [];
    const currentChoice = currentChoices[choiceIndex] as any;
    const publishedChoice = publishedChoices[choiceIndex] as any;

    if (!currentChoice) return false;
    if (!publishedChoice) return true;

    // Strapi returns a component `id` on every nested clause row; clauses built inline have
    // none. Compare on content only — otherwise a choice that was merely loaded, never
    // edited, reads as permanently modified. DA-2184: an AUTO choice is authored on its READ
    // intent alone (the BE derives write/unselect on save), so compare AUTO clauses on their
    // READ rows only — symmetric with the payload filter and getQuestionDiff — or a legacy
    // row's WRITE/UNSELECT clauses would read as a phantom change.
    const normalizeClauses = (choice: any, clauses?: any[]) => {
      const stripped = (clauses || []).map((c) => omit(c, 'id'));
      return choice.mapperSource === 'AUTO' ? stripped.filter((c: any) => c.role === 'READ') : stripped;
    };

    return (
      !isEqual(currentChoice.mapperSource, publishedChoice.mapperSource) ||
      !isEqual(normalizeClauses(currentChoice, currentChoice.hpMapperClauses), normalizeClauses(publishedChoice, publishedChoice.hpMapperClauses)) ||
      !isEqual(normalizeClauses(currentChoice, currentChoice.apMapperClauses), normalizeClauses(publishedChoice, publishedChoice.apMapperClauses))
    );
  }

  // Helper function to check if a specific subchoice field is modified (handles localization)
  const isSubChoiceFieldModified = (
    choiceIndex: number,
    subChoiceIndex: number,
    fieldName: keyof import('types/apis/surveyInterfaces').SubChoice
  ): boolean => {
    if (!question || !publishedQuestion) {
      return false;
    }

    // For title field, compare locale-specific values
    if (fieldName === 'title') {
      const currentTitle = answerChoices[choiceIndex]?.subChoices?.[subChoiceIndex]?.title || '';
      const publishedTitle = getSubChoiceLocaleTitle(
        publishedQuestion.choices?.[choiceIndex]?.subChoices?.[subChoiceIndex] || {},
        choiceIndex,
        subChoiceIndex,
        selectedLocale,
        publishedQuestion
      );
      return currentTitle !== publishedTitle;
    }

    // For other fields, compare directly
    const formValues = getValues() as CreateSurveyQuestionRequestData;
    const currentChoices = formValues.choices || [];
    const publishedChoices = publishedQuestion.choices || [];

    if (choiceIndex >= currentChoices.length || choiceIndex >= publishedChoices.length) {
      return false;
    }

    const currentChoice = currentChoices[choiceIndex];
    const publishedChoice = publishedChoices[choiceIndex];

    if (!currentChoice || !publishedChoice) {
      return false;
    }

    const currentSubChoices = currentChoice.subChoices || [];
    const publishedSubChoices = publishedChoice.subChoices || [];

    // If subchoices array length changed, all fields in this subchoice are considered modified
    if (currentSubChoices.length !== publishedSubChoices.length) {
      return subChoiceIndex < currentSubChoices.length;
    }

    // If subchoice at this index doesn't exist in published, it's new/modified
    if (subChoiceIndex >= publishedSubChoices.length) {
      return true;
    }

    const currentSubChoice = currentSubChoices[subChoiceIndex];
    const publishedSubChoice = publishedSubChoices[subChoiceIndex];

    if (!currentSubChoice || !publishedSubChoice) {
      return false;
    }

    // Compare the specific field
    return !isEqual(currentSubChoice[fieldName], publishedSubChoice[fieldName]);
  }

  // Common sx style for modified fields (for TextField)
  const getModifiedFieldStyle = (fieldName: keyof CreateSurveyQuestionRequestData) => ({
    '& .MuiInputBase-inputMultiline' : {
      padding: '0px',
    },
    '& .MuiInputBase-input' : {
      color: theme.palette.text.secondary,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
      }
    },
    ...(isFieldModified(fieldName) && {
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  // Common sx style for modified Select fields
  const getModifiedSelectStyle = (fieldName: keyof CreateSurveyQuestionRequestData) => ({
    '& .MuiSelect-select' : {
      minHeight: 'unset',
      backgroundColor: 'inherit',
    },
    ...(isFieldModified(fieldName) && {
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  // Common sx style for modified Checkbox components
  const getModifiedCheckboxStyle = (fieldName: keyof CreateSurveyQuestionRequestData) => ({
    ...(isFieldModified(fieldName) && {
      '& .MuiCheckbox-root': {
        backgroundColor: (theme: any) => {
          if (theme.palette.mode === 'light') {
            return 'rgba(196, 255, 176, 0.5)';
          }
          return darken('rgba(196, 255, 176, 1)', 0.5);
        },
      },
    }),
  });

  // Common sx style for modified choice/subchoice fields
  const getModifiedChoiceFieldStyle = (isModified: boolean) => ({
    ...(isModified && {
      '& .MuiOutlinedInput-root': {
        backgroundColor: (theme: any) => {
          if (theme.palette.mode === 'light') {
            return 'rgba(196, 255, 176, 0.5)';
          }
          return darken('rgba(196, 255, 176, 1)', 0.5);
        },
      },
    }),
  });

  // SM-25: modified highlight for the builder entry point. Same green as every other
  // modified control, applied to the button surface rather than an input root.
  const getModifiedBuilderStyle = (isModified: boolean) => ({
    ...(isModified && {
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  // Common sx style for modified choice/subchoice Select fields
  const getModifiedChoiceSelectStyle = (isModified: boolean) => ({
    ...(isModified && {
      mt: 1,
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  return (
    <Box component="form" ref={formRef} onSubmit={handleSubmit(onFormSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      
      <Grid container spacing={2}>

        <Grid size={9}>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Question Text is required' }}
            render={({ field }) => (
              <>
                {renderFormLabel('Question Text')}
                <TextField
                  {...field}
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  required
                  disabled={isReadOnly}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    setLocaleTextValue('title', value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': getModifiedFieldStyle('title'),
                  }}
                />
              </>
            )}
          />
        </Grid>
      
        <Grid size={3}> 
          {/* Locale Selector */}
         {question ? <FormControl fullWidth size="small">
            {renderFormLabel('Locale')}
            <Select
              sx={{ 
                height: '36px', 
                fontSize: '12px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '.MuiSelect-select' : {
                  minHeight: 'unset',
                  color: theme.palette.text.secondary
                }
              }}
              value={selectedLocale || CMS_DEFAULT_LOCALE}
              onChange={(e) => handleLocaleChange(e.target.value)}
            >
              {(selectedPilot!.supportedLocales as string[]).map((locale) => (
                <MenuItem key={locale} value={locale} sx={{ color: theme.palette.text.secondary }}>
                  {locales.find(l => l.code === locale)?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> : null}
      </Grid>

      <Grid size={9}>
        {/* Info */}
        <Controller
          name="info"
          control={control}
          render={({ field }) => (
            <>
              {renderFormLabel('Info')}
              <TextField
                {...field}
                multiline
                rows={3}
                fullWidth
                size="small"
                disabled={isReadOnly}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                  setLocaleTextValue('info', value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': getModifiedFieldStyle('info'),
                }}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={3}> 
        {/* Verification Status */}
        {question && (
          <Controller
            name="verificationStatus"
            control={control}
            render={({ field }) => (
              <>
                {renderFormLabel('Verification Status')}
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  disabled={true}
                  value={field.value}
                />
              </>
            )}
          />
        )}
      </Grid>

      <Grid size={4}>
        {/* Question Type */}
        <Controller
          name="type"
          control={control}
          rules={{ required: 'Question Type is required' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.type} disabled={isReadOnly}>
              {renderFormLabel('Question Type')}
              <Select
                {...field}
                displayEmpty
                sx={getModifiedSelectStyle('type')}>
                <MenuItem value="" disabled>
                  <em>Select a type</em>
                </MenuItem>
                {QUESTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={4}>
        {/* Category */}
        <Controller
          name="category"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.category} disabled={isReadOnly}>
              {renderFormLabel('Category')}
              <Select
                {...field}
                displayEmpty
                sx={getModifiedSelectStyle('category')}>
                <MenuItem value="" disabled>
                  <em>Select a category</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.categoryId} value={category.categoryId}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={4}>
        {/* Group */}
        <Controller
          name="group"
          control={control}
          rules={{ required: 'Group is required' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.group} disabled={isReadOnly}>
              {renderFormLabel('Group')}
              <Select
                {...field}
                displayEmpty
                sx={getModifiedSelectStyle('group')}>
                <MenuItem value="" disabled>
                  <em>Select a group</em>
                </MenuItem>
                {GROUPS.map((group) => (
                  <MenuItem key={group} value={group}>
                    {getGroupDisplayText(group)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={4}>
        {/* Profile Code */}
        <Controller
          name="profileCode"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth disabled={isReadOnly}>
              {renderFormLabel('Profile Code')}
              <Select 
                {...field} 
                value={field.value}
                sx={getModifiedSelectStyle('profileCode')}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {PROFILE_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={2}>
        {/* Max Level — how deep the question may nest (levels 2-3 mark a detail/child question). */}
        <Controller
          name="maxLevel"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth disabled={isReadOnly}>
              {renderFormLabel('Max Level')}
              <Select
                {...field}
                value={field.value ?? 1}
                sx={getModifiedSelectStyle('maxLevel')}>
                {[1, 2, 3].map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Levels 2-3 mark a detail (child) question.</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={6}>
        {/* State Mapper — a question whose choices carry clauses shows the derived value
            read-only (the BE recomputes it on save); choiceless HP/AP NUMBER/2D/3D get the
            guided picker (SM-22/SM-23); everything else keeps the raw field. */}
        <Controller
          name="stateMapper"
          control={control}
          render={({ field }) => (
            <>
              {renderFormLabel('State Mapper')}
              {isMetadataProfile ? (
                <>
                  {/* METADATA has no HP/AP target — its state key is `METADATA-<question id>`,
                      and that id depends on the question's position in a template, so it can't be
                      authored here. The template API generates it on read; the field is hidden. */}
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontStyle: 'italic', color: theme.palette.text.disabled }}
                  >
                    METADATA-&lt;question id&gt;
                  </Typography>
                  <FormHelperText>
                    Generated per template when the template API is called (depends on the question&apos;s position) — not authored here.
                  </FormHelperText>
                </>
              ) : stateMapperIsDerived ? (
                <>
                  {/* DA-2184: BE-authoritative and save-only — show the stored last-saved
                      stateMapper read-only, never a live preview or an editable field. */}
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      color: question?.stateMapper ? theme.palette.text.primary : theme.palette.text.disabled,
                    }}
                  >
                    {question?.stateMapper || '—'}
                  </Typography>
                  <FormHelperText>Derived from answer choices on save</FormHelperText>
                </>
              ) : isChoicelessHpQuestion ? (
                <HpStateMapperPicker
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  catalog={mapperCatalog}
                  loading={mapperCatalogLoading}
                  error={mapperCatalogError}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': getModifiedFieldStyle('stateMapper') }}
                />
              ) : isChoicelessApQuestion ? (
                <ApStateMapperPicker
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  catalog={mapperCatalog}
                  loading={mapperCatalogLoading}
                  error={mapperCatalogError}
                  disabled={isReadOnly}
                  sx={{ '& .MuiOutlinedInput-root': getModifiedFieldStyle('stateMapper') }}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  sx={{
                    '& .MuiOutlinedInput-root': getModifiedFieldStyle('stateMapper'),
                  }}
                />
              )}
            </>
          )}
        />
      </Grid>
      
    </Grid>

      {/* DA-2301: a single always-visible catalog-error banner for any HP/AP question. The
          per-control inline errors still fire, but this guarantees the failure is surfaced once,
          clearly, while the rest of the form keeps working (the AC). METADATA needs no catalog. */}
      {mapperCatalogError && (profileCode === 'HP' || profileCode === 'AP') && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Couldn&apos;t load the mapper catalog, so the attribute/appliance pickers may be empty. You can
          still edit the rest of the question; reopen the form to retry. ({mapperCatalogError})
        </Alert>
      )}

      {/* Mapper Scope — question-level target that restricts every choice's picker (DA-2184).
          Placed above Fuel Type so the appliance/attribute scope is set before authoring choices. */}
      {showChoicesSection && (profileCode === 'HP' || profileCode === 'AP') && !isReadOnly && (
        <QuestionScopeSelector
          profileCode={profileCode}
          value={effectiveScope}
          catalog={mapperCatalog}
          loading={mapperCatalogLoading}
          error={mapperCatalogError}
          applianceAllowlist={categoryApplianceIds}
          onChange={handleScopeChange}
        />
      )}

      {/* Fuel Types */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Fuel Type
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isGlobal}
                onChange={(e) => handleGlobalChange(e.target.checked)}
                disabled={isReadOnly}
                sx={getModifiedCheckboxStyle('fuelTypes')}
              />
            }
            label="GLOBAL"
          />
          <Box sx={{ display: 'flex', gap: 2, backgroundColor: theme.palette.background.default, borderRadius : '8px'}}>
            {FUEL_TYPES.map((fuelType) => (
              <FormControlLabel
                key={fuelType}
                control={
                  <Checkbox
                    checked={selectedFuelTypes.includes(fuelType)}
                    onChange={(e) => handleFuelTypeChange(fuelType, e.target.checked)}
                    disabled={isReadOnly || isGlobal}
                    sx={{
                      marginLeft: '10px',
                      ...getModifiedCheckboxStyle('fuelTypes')
                    }}
                  />
                }
                label={fuelType}
              />
            ))}
          </Box>
        </Box>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Select either GLOBAL or one or more specific fuel types.
        </Typography>
      </Box>

      {/* Impacts */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Impacts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="impactShc"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox 
                    {...field} 
                    checked={field.value} 
                    disabled={isReadOnly}
                    sx={getModifiedCheckboxStyle('impactShc')}
                  />
                }
                label="SHC"
              />
            )}
          />
          <Controller
            name="impactItemization"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox 
                    {...field} 
                    checked={field.value} 
                    disabled={isReadOnly}
                    sx={getModifiedCheckboxStyle('impactItemization')}
                  />
                }
                label="Itemization"
              />
            )}
          />
          <Controller
            name="impactReco"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox 
                    {...field} 
                    checked={field.value} 
                    disabled={isReadOnly}
                    sx={getModifiedCheckboxStyle('impactReco')}
                  />
                }
                label="Recommendations"
              />
            )}
          />
        </Box>
      </Box>

      {/* Answer Choices — hidden entirely for METADATA (SM-18) */}
      {showChoicesSection && (
        <>
          {canConvertToStructured && (
            <ConvertToStructuredBanner
              choices={answerChoices}
              stateMapper={watch('stateMapper')}
              onConverted={handleChoicesChange}
              disabled={isReadOnly}
            />
          )}
          <AnswerChoicesSection
            choices={answerChoices}
            onChoicesChange={handleChoicesChange}
            questionType={questionType}
            profileCode={profileCode}
            mapperScope={effectiveScope}
            catalog={mapperCatalog}
            catalogLoading={mapperCatalogLoading}
            catalogError={mapperCatalogError}
            isReadOnly={isReadOnly}
            errors={errors}
            isChoiceFieldModified={isChoiceFieldModified}
            isChoiceMapperModified={isChoiceMapperModified}
            isSubChoiceFieldModified={isSubChoiceFieldModified}
            getModifiedChoiceFieldStyle={getModifiedChoiceFieldStyle}
            getModifiedChoiceSelectStyle={getModifiedChoiceSelectStyle}
            getModifiedBuilderStyle={getModifiedBuilderStyle}
          />
          {answerChoices.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Add answer choices
            </Typography>
          )}
        </>
      )}

      {isMetadataProfile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          METADATA questions store a free-text / number answer as survey metadata — no answer
          choices or read / write mappers to configure.
        </Typography>
      )}

    </Box>
  )
}

